import { createContext, useContext } from 'react';

/**
 * 팀 목록이 바뀌었다고 **본문이 셸에 알리는 통로** (2026-10-12).
 *
 * ## 무엇이 잘못됐나 — 사용자 원문
 *
 * ```
 * 「팀을 삭제하면 스페이스 서브메뉴에 나오지만 새로고침하지 않으면 삭제가 되지 않는다」
 * ```
 *
 * ⚠ 그 글의 「삭제가 되지 않는다」는 **뜻이 둘로 갈린다**:
 * ```
 * ⓐ 지우기가 «실패»했다        ⇒ 서버·권한 문제
 * ⓑ 지워졌는데 «목록이 낡았다»  ⇒ 화면 상태 문제
 * ⇒ 가르는 것은 「새로고침하면 사라진다」 ⇒ ✅ ⓑ 다
 *   ⇒ 그리고 서버 쪽은 `deleteTeam` 이 던지면 본문이 잡으므로, 조용히 지워진 것이다
 * ```
 *
 * ## 까닭 — 팀 목록이 «두 벌»이고 셸에게 알리는 길이 없었다
 *
 * ```
 * 셸   ProjectNavShell.tsx  useEffect(() => { void listTeams().then(setTeams) }, [])
 *      ⇒ «마운트 때 한 번»만 읽는다 ⇒ 그 값이 서브메뉴(ProjectHomeNav)로 간다
 * 본문 TeamsDetailPage.tsx  await deleteTeam(id); …; void refreshTeams()
 *      ⇒ `refreshTeams` 는 «본문 자기 상태»만 다시 읽는다
 * ⇒ ⇒ 그래서 본문에서는 사라지고 서브메뉴에는 남는다
 * ```
 *
 * ⇒ ✅ **회귀가 아니다** — `ProjectNavShell.tsx` 가 처음 생긴 `eb7cc71c0`(2026-09-30)
 *   부터 그 `useEffect([])` 가 같은 꼴이다(그 세 줄의 이력을 `git log -L` 로 쟀다).
 *   45·46차가 만든 것이 아니다.
 *
 * ## 왜 이 꼴인가 — `pages/BoardV1/boards-context.tsx` 와 «같은 집안»
 *
 * ```
 * 그 파일 머리말이 같은 증상을 적고 있다:
 *   「목록을 읽는 곳이 둘이 될 뻔했다 … 화면이 `recount()` 를 부르고
 *     셸이 다시 읽어 트리와 본문이 같은 값으로 함께 갱신된다」
 * ⇒ 쟀다 ⇒ 이 레포의 「셸 ⇄ 본문」 관용구는 셋이다:
 *     createContext(FormField · boards-context · widget-context 등)
 *     subscribe* ＋ useSyncExternalStore(i18n 통 · app-color-mode)
 *     zustand store(store/*.ts)
 *   ⇒ 뒤 둘은 «앱 전역»(언어·색 모드·로그인)이고, 이것은 «한 셸 안»의 일이다
 *     ⇒ ⇒ 그래서 boards 와 같은 createContext 로 간다 — 같은 뜻을 다른 글자로 두지 않는다
 * ```
 *
 * ## ⏳ 여기서 멈춘 것 — boards 는 «한 곳에서만 읽는다»까지 갔다
 *
 * ```
 * boards 꼴 ⇒ 셸이 «혼자» 읽고(진실의 한 곳) 본문은 context 에서 받아 쓴다
 * 이 판   ⇒ 두 벌을 그대로 두고 «신호»만 잇는다
 *   ⇒ 까닭 ⇒ `TeamsDetailPage` 는 팀 목록을 목록·선택·프로젝트 묶기에 두루 쓰고
 *     그 상태를 context 로 옮기면 그 화면의 렌더 경로를 바꾼다(소스 회귀 금지)
 *   ⇒ ⏳ 만료 ⇒ 그 화면을 손볼 일이 따로 생기면 그때 boards 꼴로 모은다
 *     ⇒ 그때 재야 할 것 ⇒ 「listTeams 를 부르는 제품 파일이 셸 하나뿐」
 * ```
 */
export interface ProjectTeamsSignal {
  /**
   * 팀 목록이 바뀌었다 — 셸이 다시 읽어 **서브메뉴와 본문이 함께** 새 값을 받는다.
   *
   * ⚠ 본문이 자기 목록을 다시 읽는 것(`refreshTeams`)을 **대신하지 않는다**. 둘 다 부른다
   *   (이 판은 두 벌을 그대로 두고 신호만 이었다 — 위 ⏳).
   */
  teamsChanged: () => void;
}

/**
 * 기본값은 **아무것도 하지 않는다**.
 *
 * ⚠ 까닭 — 셸 밖에서 그려지는 화면이 있다(`/task` 등은 이 셸을 안 쓴다). 그때 `undefined`
 *   를 돌려주면 호출자가 `?.()` 를 붙여야 하고, 빠뜨리면 조용히 안 돈다.
 *   ⇒ 그래서 **빈 함수**를 기본으로 두어 호출자가 늘 그냥 부를 수 있게 한다.
 *   ⇒ ⚠ 대신 「정말 셸 안인가」는 가드가 «셸이 Provider 로 감싸나»로 잰다.
 */
export const ProjectTeamsContext = createContext<ProjectTeamsSignal>({
  teamsChanged: () => {},
});

export function useProjectTeamsSignal(): ProjectTeamsSignal {
  return useContext(ProjectTeamsContext);
}
